import { useState, useRef, useEffect } from "react";
import { X, Sparkles, Copy, RotateCcw, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { streamAI } from "@/lib/aiStream";

const TONES = ["friendly", "direct", "premium"];

/** Claude-drafted first reply to an unlocked lead. Streams token by token. */
export default function BidDraftModal({ job, onClose, onSent }) {
  const [tone, setTone] = useState("friendly");
  const [notes, setNotes] = useState("");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const generate = async () => {
    setBusy(true);
    setDraft("");
    abortRef.current = new AbortController();
    try {
      await streamAI(
        "/ai/bid-draft",
        { job_id: job.job_id, tone, notes: notes || null },
        (full) => setDraft(full),
        abortRef.current.signal
      );
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message || "Could not draft a reply");
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setSending(true);
    try {
      await api.post("/messages", { job_id: job.job_id, to_user_id: job.customer_id, text: draft.trim() });
      toast.success("Reply sent to the homeowner.");
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6" data-testid="bid-draft-modal">
      <div className="brut-card bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b-2 border-ink p-5 sticky top-0 bg-acid">
          <div>
            <div className="overline">// AI bid assistant</div>
            <h3 className="font-display font-black uppercase text-xl tracking-tighter mt-1 line-clamp-1">{job.title}</h3>
          </div>
          <button onClick={onClose} className="border-2 border-ink w-8 h-8 flex items-center justify-center bg-white" data-testid="bid-draft-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <label className="overline block mb-2">Tone</label>
            <div className="grid grid-cols-3 gap-0 border-2 border-ink">
              {TONES.map((t) => (
                <button key={t} type="button" onClick={() => setTone(t)}
                  className={`py-3 font-mono uppercase text-xs tracking-widest font-bold border-l-2 first:border-l-0 border-ink ${tone === t ? "bg-ink text-acid" : "bg-white"}`}
                  data-testid={`bid-tone-${t}`}>{t}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="overline block mb-2">Anything to include? (optional)</label>
            <input className="brut-input" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Free estimates, can start Monday, 20 yrs licensed..." data-testid="bid-notes" />
          </div>

          <button onClick={generate} disabled={busy} className="brut-btn-primary w-full disabled:opacity-40" data-testid="bid-generate">
            <Sparkles className={`w-4 h-4 mr-2 ${busy ? "animate-pulse" : ""}`} />
            {busy ? "Claude is writing..." : draft ? "Regenerate" : "Draft my reply"}
          </button>

          {(draft || busy) && (
            <div>
              <label className="overline block mb-2">Your reply — edit before sending</label>
              <textarea className="brut-input min-h-[240px] font-mono text-xs leading-relaxed"
                value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="bid-draft-text" />
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={send} disabled={busy || sending || !draft.trim()} className="brut-btn-acid flex-1 disabled:opacity-40" data-testid="bid-send">
                  <Send className="w-3 h-3 mr-2" /> {sending ? "Sending..." : "Send in chat"}
                </button>
                <button onClick={() => { navigator.clipboard.writeText(draft); toast.success("Copied"); }}
                  disabled={!draft} className="brut-btn disabled:opacity-40" data-testid="bid-copy">
                  <Copy className="w-3 h-3 mr-2" /> Copy
                </button>
                <button onClick={() => setDraft("")} disabled={busy || !draft} className="brut-btn disabled:opacity-40" data-testid="bid-clear">
                  <RotateCcw className="w-3 h-3 mr-2" /> Clear
                </button>
              </div>
            </div>
          )}

          <p className="font-mono text-[10px] text-muted-foreground">
            Drafted by Claude from the lead details and your profile. Always check prices and dates before sending.
          </p>
        </div>
      </div>
    </div>
  );
}
